import { Drawer, Box, Inline, Loader, Bleed } from 'braid-design-system';

import profileHeroBg from '../../images/profile-header-shapes.svg';
import type { ProfileBase, ProfileView } from '../../types/profile.types';
import type { LoginPromptProps } from '../GenericLoginPrompt/GenericLoginPrompt';
import { GenericProfileError } from '../GenericProfileError/GenericProfileError';
import {
  SignedInProfileView,
  type TabChangeProps,
  type TabContent,
} from '../ProfileView/SignedInProfileView/SignedInProfileView';
import { SignedOutProfileView } from '../SignedOutProfileView/SignedOutProfileView';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  submitProfileReport?: (
    reason: string,
    comment: string,
    profileSlug: string,
  ) => Promise<boolean>;
  isAuthenticated: boolean;
  profile: ProfileView | null;
  tabs: TabContent[];
  tabChangeProps?: TabChangeProps;
  profileUrl?: string;
  isLoading: boolean;
  hasError: boolean;
  authUrls: {
    loginUrl: string;
    googleLoginUrl: string;
    registerUrl: string;
  };
  trackingCallbacks: {
    profileDisplayedCallback: () => void;
  } & LoginPromptProps['trackingCallbacks'];
  renderCardActions?: (profile: ProfileBase) => React.ReactNode;
  renderWidget?: () => React.ReactNode;
}

const ProfileDrawerLoading = () => (
  <Box paddingY="xlarge">
    <Inline space="none" align="center">
      <Loader />
    </Inline>
  </Box>
);

const ProfileDrawerError = () => (
  <Bleed
    top="xxlarge"
    horizontal={{ mobile: 'small', tablet: 'medium', desktop: 'gutter' }}
  >
    <Box
      background="brand"
      overflow="hidden"
      style={{
        height: '160px',
        position: 'relative',
      }}
      id="profile-error"
    >
      <div
        dangerouslySetInnerHTML={{ __html: profileHeroBg }}
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          height: '100%',
        }}
      />
    </Box>
    <Box marginTop="xxlarge">
      <GenericProfileError />
    </Box>
  </Bleed>
);

export const ProfileDrawer = ({
  isOpen,
  onClose,
  isAuthenticated,
  profile,
  tabs,
  tabChangeProps,
  profileUrl,
  isLoading,
  hasError,
  authUrls,
  trackingCallbacks,
  submitProfileReport,
  renderCardActions,
  renderWidget,
}: Props) => {
  const renderContent = () => {
    if (isLoading) {
      return <ProfileDrawerLoading />;
    }

    if (hasError) {
      return <ProfileDrawerError />;
    }

    if (!profile) {
      return;
    }

    return (
      <Bleed
        top="xxlarge"
        horizontal={{ mobile: 'small', tablet: 'medium', desktop: 'gutter' }}
      >
        <Box
          background="brand"
          overflow="hidden"
          style={{
            height: '160px',
            position: 'relative',
          }}
        >
          <div
            dangerouslySetInnerHTML={{ __html: profileHeroBg }}
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              height: '100%',
            }}
          />
        </Box>
        <Box padding="gutter">
          {isAuthenticated ? (
            <SignedInProfileView
              profile={profile}
              profileUrl={profileUrl || ''}
              tabs={tabs}
              tabChangeProps={tabChangeProps}
              renderCardActions={renderCardActions}
              submitProfileReport={submitProfileReport}
              trackingCallbacks={trackingCallbacks}
              renderWidget={renderWidget}
            />
          ) : (
            <SignedOutProfileView
              profile={profile}
              authUrls={authUrls}
              trackingCallbacks={trackingCallbacks}
            />
          )}
        </Box>
      </Bleed>
    );
  };

  return (
    <Drawer
      id="candidate-profile-drawer"
      title=""
      width="medium"
      open={isOpen}
      onClose={onClose}
    >
      {renderContent()}
    </Drawer>
  );
};
